import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import SalesOrderModal from "../components/SalesOrderModal";
import SalesOrderItemModal from "../components/SalesOrderItemModal";
import {
  cancelSalesOrder,
  cancelSalesOrderItem,
  createProductionOrderFromSalesItem,
  deleteSalesOrder,
  getSalesOrder,
  listSalesOrders,
} from "../api/salesOrders";
import { EmptyState, PageHeader } from "../components/ui";

function formatDateOnly(value) {
  if (!value) return "";
  return String(value).split("T")[0];
}

function formatInteger(value) {
  const number = Number(value);
  return Number.isFinite(number) ? String(Math.trunc(number)) : "";
}

// Kalemin durumu: iş emri açıldı mı? (İptal edilen iş emri yeniden açmaya engel değil.)
const WORK_ORDER_STATE_STYLES = {
  created: { label: "Oluşturuldu", bg: "rgba(34, 197, 94, 0.2)", fg: "#166534" },
  none: { label: "Oluşturulmadı", bg: "rgba(239, 68, 68, 0.2)", fg: "#991B1B" },
  cancelled: { label: "İptal Edildi", bg: "rgba(148, 163, 184, 0.25)", fg: "#334155" },
};

function workOrderStateChip(item) {
  const state = WORK_ORDER_STATE_STYLES[item?.work_order_state] || WORK_ORDER_STATE_STYLES.none;
  return (
    <Chip
      size="small"
      label={state.label}
      sx={{ bgcolor: state.bg, color: state.fg, fontWeight: 800, borderRadius: 1.5 }}
    />
  );
}

// Siparişin ilerleme durumu: kalemlerinin üretim/sevk durumundan türetilir.
const PROGRESS_STATE_STYLES = {
  not_started: { label: "Başlanmadı", bg: "rgba(239, 68, 68, 0.2)", fg: "#991B1B" },
  in_production: { label: "Üretimde", bg: "rgba(249, 115, 22, 0.22)", fg: "#9A3412" },
  partial: { label: "Kısmi Sevk", bg: "rgba(234, 179, 8, 0.28)", fg: "#854D0E" },
  completed: { label: "Bitti", bg: "rgba(34, 197, 94, 0.2)", fg: "#166534" },
};

function progressStateChip(order) {
  const state = PROGRESS_STATE_STYLES[order?.progress_state] || PROGRESS_STATE_STYLES.not_started;
  return (
    <Chip
      size="small"
      label={state.label}
      sx={{ bgcolor: state.bg, color: state.fg, fontWeight: 800, borderRadius: 1.5 }}
    />
  );
}

// Satır numarasının üzerine gelince varyantı adlandırılmış olarak gösterir.
// (İş emirleri sayfasındaki İş Emri No tooltip'iyle aynı desen.)
function lineNoCell(item) {
  const details = Array.isArray(item?.variant_details) ? item.variant_details : [];
  const materialName = item?.material_name || "";
  if (!details.length && !materialName) return item?.line_no;

  return (
    <Tooltip
      arrow
      placement="right"
      title={
        <div>
          {materialName ? (
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{materialName}</div>
          ) : null}
          {details.map((detail, index) => (
            <div key={`${detail?.label || "dim"}-${index}`} style={{ whiteSpace: "nowrap" }}>
              <span style={{ opacity: 0.75 }}>{detail?.label}:</span>{" "}
              <b>{detail?.value}{detail?.unit ? ` ${detail.unit}` : ""}</b>
            </div>
          ))}
        </div>
      }
    >
      <span style={{ cursor: "help", textDecoration: "underline dotted" }}>{item?.line_no}</span>
    </Tooltip>
  );
}

export default function SalesOrders() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [openAddItem, setOpenAddItem] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [ordersPage, setOrdersPage] = useState(0);
  const [itemsPage, setItemsPage] = useState(0);
  const [filterOrderNo, setFilterOrderNo] = useState("");
  const [filterCustomerName, setFilterCustomerName] = useState("");

  const ORDERS_PER_PAGE = 10;
  const ITEMS_PER_PAGE = 20;

  function loadList() {
    return listSalesOrders()
      .then((orders) => {
        setList(orders);
        setOrdersPage(0);
      })
      .catch((err) => {
        console.error("SALES ORDERS LOAD ERROR", err);
        alert("Satış siparişleri yüklenemedi");
      });
  }

  function loadDetail(id) {
    if (!id) return;
    setLoadingDetail(true);
    getSalesOrder(id)
      .then((d) => setDetail(d))
      .catch((err) => {
        console.error("SALES ORDER DETAIL LOAD ERROR", err);
        alert("Satış siparişi detayı yüklenemedi");
      })
      .finally(() => setLoadingDetail(false));
  }

  useEffect(() => {
    loadList();
  }, []);

  useEffect(() => {
    if (selectedId) {
      setSelectedItemId(null);
      setItemsPage(0);
      loadDetail(selectedId);
    }
  }, [selectedId]);

  useEffect(() => {
    setOrdersPage(0);
  }, [filterOrderNo, filterCustomerName]);

  const filteredOrders = useMemo(() => {
    const orderNoQuery = filterOrderNo.trim().toLocaleLowerCase("tr-TR");
    const customerQuery = filterCustomerName.trim().toLocaleLowerCase("tr-TR");

    return list.filter((order) => {
      const orderNo = String(order.order_no || "").toLocaleLowerCase("tr-TR");
      const customerName = String(order.customer_name || "").toLocaleLowerCase("tr-TR");
      return orderNo.includes(orderNoQuery) && customerName.includes(customerQuery);
    });
  }, [list, filterOrderNo, filterCustomerName]);

  const paginatedOrders = useMemo(() => {
    const start = ordersPage * ORDERS_PER_PAGE;
    return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
  }, [filteredOrders, ordersPage]);

  const itemsSortedAndPaginated = useMemo(() => {
    const items = detail?.items || [];
    const sorted = [...items].sort((a, b) => {
      const aDate = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
      const bDate = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
      return bDate - aDate;
    });
    const start = itemsPage * ITEMS_PER_PAGE;
    return sorted.slice(start, start + ITEMS_PER_PAGE);
  }, [detail?.items, itemsPage]);

  const itemsTotalCount = (detail?.items || []).length;

  const selectedItem = useMemo(() => {
    const items = detail?.items || [];
    return items.find((x) => Number(x.id) === Number(selectedItemId)) || null;
  }, [detail, selectedItemId]);

  async function handleOpenWorkOrder(item) {
    try {
      const res = await createProductionOrderFromSalesItem({
        sales_order_id: Number(detail.id),
        sales_order_item_id: Number(item.id),
      });
      alert(`İş emri açıldı. ID: ${res.id}`);
      navigate("/work-orders");
    } catch (err) {
      console.error("CREATE WORK ORDER ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      alert(
        `İş emri açılamadı${status ? ` (HTTP ${status})` : ""}${
          message ? `: ${message}` : " (rota/BOM kontrol edin)"
        }`
      );
    }
  }

  async function handleCancelOrder() {
    if (!detail?.id) return;
    if (!window.confirm("Sipariş iptal edilsin mi?")) return;
    try {
      await cancelSalesOrder(Number(detail.id));
      await loadList();
      await loadDetail(Number(detail.id));
    } catch (err) {
      console.error("CANCEL ORDER ERROR", err);
      alert("Sipariş iptal edilemedi");
    }
  }

  async function handleDeleteOrder() {
    if (!detail?.id) return;
    if (!window.confirm(`"${detail.order_no}" siparişi ve tüm kalemleri kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await deleteSalesOrder(Number(detail.id));
      setSelectedId(null);
      setDetail(null);
      loadList();
    } catch (err) {
      console.error("DELETE ORDER ERROR", err);
      alert("Sipariş silinemedi");
    }
  }

  async function handleCancelItem(item) {
    if (!window.confirm("Kalem iptal edilsin mi?")) return;
    try {
      await cancelSalesOrderItem(Number(detail.id), Number(item.id));
      loadDetail(Number(detail.id));
    } catch (err) {
      console.error("CANCEL ITEM ERROR", err);
      alert("Kalem iptal edilemedi");
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1540, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}>
      <PageHeader
        title="Satış Siparişleri"
        subtitle="Siparişleri oluşturun, kalem ekleyin ve iş emrine dönüştürün."
        actions={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ p: 1, borderRadius: 3, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 8px 24px rgba(79, 70, 229, 0.08)" }}>
          <Button variant="contained" color="success" onClick={() => setOpenCreate(true)} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none" }}>
            + Yeni Satış Siparişi
          </Button>
            <Button
              variant="contained"
              onClick={() => handleOpenWorkOrder(selectedItem)}
              disabled={
                !selectedItem ||
                selectedItem.status !== "active" ||
                selectedItem.work_order_state === "created"
              }
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none", bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } }}
            >
              İş Emri Oluştur
            </Button>
            <Tooltip
              title={
                selectedItem && selectedItem.work_order_state === "created"
                  ? "İş emri açılmış kalem düzenlenemez."
                  : ""
              }
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={() => setEditItem(selectedItem)}
                  disabled={
                    !selectedItem ||
                    detail?.status !== "open" ||
                    selectedItem.status !== "active" ||
                    selectedItem.work_order_state === "created"
                  }
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                >
                  Kalemi Düzenle
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleCancelItem(selectedItem)}
              disabled={!selectedItem || detail?.status !== "open" || selectedItem.status !== "active"}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none" }}
            >
              Kalemi İptal
            </Button>
            <TextField
              size="small"
              label="Sipariş No"
              value={filterOrderNo}
              onChange={(event) => setFilterOrderNo(event.target.value)}
            />
            <TextField
              size="small"
              label="Müşteri Adı"
              value={filterCustomerName}
              onChange={(event) => setFilterCustomerName(event.target.value)}
            />
          </Stack>
        }
      />

      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          alignItems: "flex-start",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box sx={{ width: { xs: "100%", sm: "32%" }, flexShrink: 0 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>
              Liste
            </Typography>

            {!list.length ? (
              <EmptyState title="Sipariş yok" description="Henüz satış siparişi oluşturulmadı." actionLabel="+ Yeni Sipariş" onAction={() => setOpenCreate(true)} />
            ) : (
              <Table size="small" sx={{ bgcolor: "#fff", borderRadius: 2.5, overflow: "hidden", "& .MuiTableCell-head": { bgcolor: "#ede9fe", color: "#3730a3", fontWeight: 800, borderColor: "#ddd6fe" }, "& .MuiTableCell-root": { borderColor: "#e5e7eb" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>Müşteri</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedOrders.map((o) => (
                    <TableRow
                      key={o.id}
                      hover
                      selected={Number(selectedId) === Number(o.id)}
                      onClick={() => setSelectedId(Number(o.id))}
                      sx={{
                        cursor: "pointer",
                        "&.Mui-selected, &.Mui-selected:hover": {
                          backgroundColor: "#c4b5fd",
                          "& .MuiTableCell-root": { color: "#1e1b4b", fontWeight: 700 },
                        },
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 800 }}>{o.order_no}</Typography>
                      </TableCell>
                      <TableCell>{o.customer_name || ""}</TableCell>
                      <TableCell>
                        {progressStateChip(o)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {filteredOrders.length > ORDERS_PER_PAGE ? (
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        count={filteredOrders.length}
                        page={ordersPage}
                        onPageChange={(_, newPage) => setOrdersPage(newPage)}
                        rowsPerPage={ORDERS_PER_PAGE}
                        rowsPerPageOptions={[ORDERS_PER_PAGE]}
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                        colSpan={3}
                        sx={{ borderBottom: "none" }}
                      />
                    </TableRow>
                  </TableFooter>
                ) : null}
              </Table>
            )}
          </Paper>
        </Box>

        <Box sx={{ width: { xs: "100%", sm: "68%" }, flexShrink: 0 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)" }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: "#312e81" }}>
                Detay
              </Typography>
              {detail?.id ? (
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" color="error" onClick={handleCancelOrder} disabled={detail.status !== "open"}>
                    Siparişi İptal
                  </Button>
                  <Button variant="contained" color="error" onClick={handleDeleteOrder}>
                    Sil
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            {!selectedId ? (
              <EmptyState title="Detay yok" description="Detay için soldan bir sipariş seçin." />
            ) : loadingDetail ? (
              <Typography variant="body2" color="text.secondary">
                Yükleniyor...
              </Typography>
            ) : !detail ? (
              <EmptyState title="Detay yok" description="Seçili sipariş için detay bulunamadı." />
            ) : (
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ p: 1.5, borderRadius: 3, bgcolor: "#fff", border: "1px solid #ddd6fe" }}>
                  <Typography sx={{ fontWeight: 800 }}>
                    {detail.order_no} {detail.customer_name ? `- ${detail.customer_name}` : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Injet No: {detail.injet_no || "-"}
                  </Typography>
                  <Button variant="contained" onClick={() => setOpenAddItem(true)} disabled={detail.status !== "open"} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, boxShadow: "none", bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } }}>
                    + Kalem Ekle
                  </Button>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, bgcolor: "#fff", border: "1px solid #ddd6fe" }}>
                      <Typography sx={{ fontWeight: 900, mb: 1.5, color: "#312e81" }}>Kalemler</Typography>
                      <Table size="small" sx={{ "& .MuiTableCell-head": { bgcolor: "#ede9fe", color: "#3730a3", fontWeight: 800, borderColor: "#ddd6fe" }, "& .MuiTableCell-root": { borderColor: "#e5e7eb" } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Satır</TableCell>
                            <TableCell>Malzeme</TableCell>
                            <TableCell>Müşteri Ürün Kodu</TableCell>
                            <TableCell>Miktar (Paket)</TableCell>
                            <TableCell>Termin</TableCell>
                            <TableCell>Durum</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {itemsSortedAndPaginated.map((it) => (
                            <TableRow
                              key={it.id}
                              hover
                              selected={Number(selectedItemId) === Number(it.id)}
                              onClick={() => setSelectedItemId(Number(it.id))}
                              sx={{
                                cursor: "pointer",
                                // Düzenlenmiş kalem sarı zeminle işaretlenir; yazı renkleri değişmez.
                                ...(Number(it.change_count) > 0
                                  ? { backgroundColor: "#fef3c7", "&:hover": { backgroundColor: "#fde68a" } }
                                  : null),
                                "&.Mui-selected, &.Mui-selected:hover": {
                          backgroundColor: "#c4b5fd",
                          "& .MuiTableCell-root": { color: "#1e1b4b", fontWeight: 700 },
                                },
                              }}
                            >
                              <TableCell>{lineNoCell(it)}</TableCell>
                              <TableCell>
                                {it.material_code} - {it.material_name}
                              </TableCell>
                              <TableCell>{it.customer_product_code || "—"}</TableCell>
                              <TableCell>{formatInteger(it.qty)}</TableCell>
                              <TableCell>{formatDateOnly(it.due_date)}</TableCell>
                              <TableCell>{workOrderStateChip(it)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        {itemsTotalCount > ITEMS_PER_PAGE ? (
                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              count={itemsTotalCount}
                              page={itemsPage}
                              onPageChange={(_, newPage) => setItemsPage(newPage)}
                              rowsPerPage={ITEMS_PER_PAGE}
                              rowsPerPageOptions={[ITEMS_PER_PAGE]}
                              labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} / ${count}`
                              }
                              colSpan={6}
                              sx={{ borderBottom: "none" }}
                            />
                          </TableRow>
                        </TableFooter>
                        ) : null}
                      </Table>
                    </Paper>
                  </Grid>

                </Grid>
              </Stack>
            )}
          </Paper>
        </Box>
      </Box>

      {openCreate ? (
        <SalesOrderModal
          onClose={() => setOpenCreate(false)}
          onSaved={() => {
            setOpenCreate(false);
            loadList();
          }}
        />
      ) : null}

      {openAddItem && detail ? (
        <SalesOrderItemModal
          salesOrderId={Number(detail.id)}
          onClose={() => setOpenAddItem(false)}
          onSaved={() => {
            setOpenAddItem(false);
            loadDetail(Number(detail.id));
          }}
        />
      ) : null}

      {editItem && detail ? (
        <SalesOrderItemModal
          salesOrderId={Number(detail.id)}
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={() => {
            setEditItem(null);
            loadDetail(Number(detail.id));
          }}
        />
      ) : null}

    </Stack>
  );
}
